import { z } from 'zod'
import { optionalString, requiredString } from '../../primitives'
import { MESSAGES } from '../../utils/messages'

/**
 * Información económica (sección del portal). Espejo del modelo interno IEconomicInfo:
 * propósito de la negociación + fuentes de ingreso (cada bandera con un ARRAY de
 * registros, como internamente) + montos mensuales. Datos de debida diligencia →
 * fuente única acá + persistencia estructurada.
 */

/** Una relación de dependencia (empleo). */
export const DependencyRelationSchema = z.object({
  id: optionalString(),
  company: requiredString(),
  position: requiredString(),
  seniority: optionalString()
})

/** Un negocio propio. */
export const OwnBusinessSchema = z.object({
  id: optionalString(),
  name: requiredString(),
  activity: optionalString()
})

/** Una otra fuente de ingreso. */
export const OtherIncomeSchema = z.object({
  id: optionalString(),
  type: requiredString(),
  detail: optionalString()
})

/**
 * Monto de dinero opcional. Acepta number o string (el input de moneda guarda el
 * string que escribe el cliente para no perder decimales en progreso) y lo coacciona
 * a number no-negativo; `''`/`null`/`undefined` → undefined.
 */
/** Importe OBLIGATORIO dentro de una fila agregada (string del input → number ≥ 0). */
const requiredMoney = () =>
  z.preprocess((v) => {
    if (v === '' || v == null) return undefined
    const n = typeof v === 'string' ? Number(v) : v
    return Number.isNaN(n) ? undefined : n
  }, z
    .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .nonnegative())

/**
 * Un monto mensual (moneda + monto). Compartido por la económica INDIVIDUAL y la
 * JURÍDICA: ambas guardan ingresos/egresos como ARRAY de filas (espejo del registro,
 * que admite varias fuentes). La individual default-ea la moneda a GTQ y no expone
 * selector; la jurídica sí lo muestra.
 */
export const MonthlyAmountSchema = z.object({
  id: optionalString(),
  currency: requiredString(),
  amount: requiredMoney()
})

const base = z.object({
  /** Propósito de la negociación (catálogo NegotiationPurpose). */
  purpose: requiredString(),
  otherPurpose: optionalString(),
  // Fuentes de ingreso: bandera + array de registros (multi, como el interno).
  hasDependencyRelation: z.boolean().default(false),
  dependencyRelations: z.array(DependencyRelationSchema).default([]),
  hasOwnBusiness: z.boolean().default(false),
  ownBusinesses: z.array(OwnBusinessSchema).default([]),
  hasOtherIncome: z.boolean().default(false),
  otherIncomes: z.array(OtherIncomeSchema).default([]),
  // Montos mensuales aproximados: ARRAY de filas (varias fuentes de ingreso/egreso),
  // espejo del registro. La individual no expone selector de moneda — el factory del
  // form siembra cada fila con GTQ. El auto-save guarda parciales (default []); el submit
  // exige al menos un INGRESO (ver superRefine). Egresos opcionales.
  monthlyIncomes: z.array(MonthlyAmountSchema).default([]),
  monthlyExpenses: z.array(MonthlyAmountSchema).default([])
})

/**
 * Schema ESTRICTO (submit). "Otro propósito" exige el detalle; cada fuente activada
 * exige al menos un registro (sus campos se validan por el element schema). El
 * auto-save NO usa esto (guarda parciales).
 */
export const EconomicDeclaration = base.superRefine((data, ctx) => {
  if (data.purpose === 'OTHER' && !String(data.otherPurpose ?? '').trim()) {
    ctx.addIssue({
      path: ['otherPurpose'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  // Al menos un ingreso mensual con monto: el perfil económico (KYC) no puede quedar sin
  // un ingreso declarado. Los egresos quedan opcionales. Cada fila ya valida su `amount`
  // por el element schema; acá sólo se exige que exista la primera.
  if (data.monthlyIncomes.length === 0) {
    ctx.addIssue({
      path: ['monthlyIncomes'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  if (data.hasDependencyRelation && data.dependencyRelations.length === 0) {
    ctx.addIssue({
      path: ['dependencyRelations'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  if (data.hasOwnBusiness && data.ownBusinesses.length === 0) {
    ctx.addIssue({
      path: ['ownBusinesses'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
  if (data.hasOtherIncome && data.otherIncomes.length === 0) {
    ctx.addIssue({
      path: ['otherIncomes'],
      code: z.ZodIssueCode.custom,
      message: MESSAGES.REQUIRED
    })
  }
})

/**
 * La forma de la declaración económica individual SIN las reglas cruzadas.
 *
 * `EconomicDeclaration` es un schema de ENTRADA: su `superRefine` exige coherencia entre
 * campos al guardar. Un contrato de LECTURA devuelve lo que hay guardado —
 * incluidos los expedientes viejos que se escribieron antes de que esa regla
 * existiera—, así que consume la base y no la versión refinada.
 */
export const EconomicDeclarationShape = base

export type DependencyRelation = z.infer<typeof DependencyRelationSchema>
export type OwnBusiness = z.infer<typeof OwnBusinessSchema>
export type OtherIncome = z.infer<typeof OtherIncomeSchema>
/** Fila de monto mensual (compartida individual/jurídica). */
export type MonthlyAmount = z.infer<typeof MonthlyAmountSchema>
export type EconomicDeclaration = z.infer<typeof base>

// ───────────────────────── Información económica JURÍDICA ─────────────────────────
/**
 * Información económica de la persona JURÍDICA. NO es una variación de la individual:
 * es un modelo distinto (espejo del FEIC interno `InfoEconomicJuridical`). En vez de
 * fuentes de ingreso personales (dependencia/negocio), describe la ENTIDAD: relación de
 * negocios, tamaño (empleados/oficinas), proveedores, clientes, fuentes de fondos y
 * montos mensuales por moneda. Datos de debida diligencia → fuente única acá.
 */

/** Entero OBLIGATORIO no negativo (cantidad de empleados / oficinas). */
const requiredCount = () =>
  z.preprocess((v) => {
    if (v === '' || v == null) return undefined
    const n = typeof v === 'string' ? Number(v) : v
    return Number.isNaN(n) ? undefined : n
  }, z
    .number({ required_error: MESSAGES.REQUIRED, invalid_type_error: MESSAGES.REQUIRED })
    .int()
    .nonnegative())

/** Un proveedor o cliente de la entidad (nombre + país). */
export const JuridicalCounterpartySchema = z.object({
  id: optionalString(),
  name: requiredString(),
  country: requiredString()
})

/** Una fuente de fondos de la entidad (catálogo SourceFunds; si OTHER, detalle aparte). */
export const JuridicalSourceFundSchema = z.object({
  id: optionalString(),
  /** Valor del catálogo de fuentes ('1'..'9'; '9' = Otro). */
  type: requiredString(),
  /** Detalle cuando `type` es "Otro". */
  otherType: optionalString(),
  description: optionalString(),
  /** ¿Recibe fondos del Estado por esta fuente? */
  receivesStateFunds: z.boolean().default(false)
})

/** Un monto mensual por moneda (ingreso o egreso de la entidad). Alias del compartido. */
export const JuridicalMonthlyAmountSchema = MonthlyAmountSchema

const juridicalBase = z.object({
  /** Propósito de la relación de negocios. */
  businessRelationship: requiredString(),
  numberEmployees: requiredCount(),
  numberOffices: requiredCount(),
  suppliers: z.array(JuridicalCounterpartySchema).default([]),
  clients: z.array(JuridicalCounterpartySchema).default([]),
  sourceFunds: z.array(JuridicalSourceFundSchema).default([]),
  monthlyIncomes: z.array(JuridicalMonthlyAmountSchema).default([]),
  monthlyExpenses: z.array(JuridicalMonthlyAmountSchema).default([])
})

/**
 * Schema ESTRICTO jurídico (submit). Cada fila agregada se valida por su element schema.
 * Una fuente de fondos "Otro" exige su detalle. El auto-save NO usa esto (guarda parciales).
 */
export const EconomicDeclarationJuridical = juridicalBase.superRefine((data, ctx) => {
  data.sourceFunds.forEach((sf, i) => {
    // '9' = "Otro" en el catálogo de fuentes de fondos: exige el detalle.
    if (sf.type === '9' && !String(sf.otherType ?? '').trim()) {
      ctx.addIssue({
        path: ['sourceFunds', i, 'otherType'],
        code: z.ZodIssueCode.custom,
        message: MESSAGES.REQUIRED
      })
    }
  })
})

/**
 * La forma de la declaración económica de una persona jurídica SIN las reglas cruzadas.
 *
 * `EconomicDeclarationJuridical` es un schema de ENTRADA: su `superRefine` exige coherencia entre
 * campos al guardar. Un contrato de LECTURA devuelve lo que hay guardado —
 * incluidos los expedientes viejos que se escribieron antes de que esa regla
 * existiera—, así que consume la base y no la versión refinada.
 */
export const EconomicDeclarationJuridicalShape = juridicalBase

export type JuridicalCounterparty = z.infer<typeof JuridicalCounterpartySchema>
export type JuridicalSourceFund = z.infer<typeof JuridicalSourceFundSchema>
export type JuridicalMonthlyAmount = z.infer<typeof JuridicalMonthlyAmountSchema>
export type EconomicDeclarationJuridical = z.infer<typeof juridicalBase>
